import React from "react";
import { useNavigate } from "react-router-dom";
import * as amplitude from "@amplitude/analytics-browser";
import { Helmet } from "react-helmet-async";
import {
  Button,
  Chip,
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Container,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import SpaIcon from "@mui/icons-material/Spa";
import SanitizerIcon from "@mui/icons-material/Sanitizer";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DiscordIcon from "@mui/icons-material/Forum";
import VideocamIcon from "@mui/icons-material/Videocam";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LinkIcon from "@mui/icons-material/Link";

function Home() {
  const navigate = useNavigate();

  // Add structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "The Wart Guy - Plantar Wart Treatment Solution",
    description:
      "A proven 60-day protocol to eliminate stubborn plantar warts using salicylic acid and tea tree oil.",
    mainEntity: {
      "@type": "HowTo",
      name: "60-Day Plantar Wart Treatment Protocol",
      description:
        "How to eliminate stubborn plantar warts at home after failed medical treatments",
      totalTime: "P60D",
    },
  };

  return (
    <>
      <Helmet>
        <title>The Wart Guy - 60-Day Plantar Wart Treatment Protocol</title>
        <meta
          name="description"
          content="A proven 60-day protocol to eliminate stubborn plantar warts using salicylic acid and tea tree oil. See how I overcame 6 years of failed medical treatments."
        />
        <meta
          name="keywords"
          content="plantar warts, plantar wart treatment, salicylic acid, tea tree oil, wart removal, foot warts, verruca"
        />
        <meta
          property="og:title"
          content="The Wart Guy - 60-Day Plantar Wart Treatment Protocol"
        />
        <meta
          property="og:description"
          content="A proven 60-day protocol to eliminate stubborn plantar warts using salicylic acid and tea tree oil."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://thewartguy.com" />

        {/* Add structured data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>

        {/* Add additional meta tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="The Wart Guy" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(160deg, #f0f9fa 0%, #e0f2f7 100%)",
            pt: { xs: 6, sm: 8, md: 12 },
            pb: { xs: 6, sm: 8, md: 12 },
            overflow: "hidden",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1.5, md: 2 },
                      color: "text.primary",
                    }}
                  >
                    The 60-Day Plantar Wart Solution
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      color: "text.secondary",
                      mb: { xs: 3, md: 4 },
                      fontWeight: 400,
                      maxWidth: "600px",
                      fontSize: { xs: "1.1rem", md: "1.3rem" },
                    }}
                  >
                    How I eliminated my stubborn plantar warts after 6 years of
                    failed medical treatments
                  </Typography>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 1.5, md: 2 }}
                    sx={{ mb: { xs: 3, md: 4 }, width: "100%" }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth={{ xs: true, sm: false }}
                      onClick={() => {
                        amplitude.logEvent("View Treatment", {
                          treatment_name: "Salicylic Acid and Tea Tree Oil",
                          location: "homepage_hero",
                        });
                        navigate("/salicylic-acid-treatment");
                      }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      See My Protocol
                    </Button>

                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      fullWidth={{ xs: true, sm: false }}
                      component="a"
                      href="https://discord.gg/Fq69xjmDVx"
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<DiscordIcon />}
                      onClick={() => {
                        amplitude.logEvent("Join Discord", {
                          location: "homepage_hero",
                        });
                      }}
                    >
                      Get Personalized Help
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      mt: { xs: 2, md: 4 },
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Chip
                      icon={<SanitizerIcon />}
                      label="Salicylic Acid"
                      color="primary"
                      variant="outlined"
                      size="medium"
                      sx={{ mb: 1 }}
                    />
                    <Chip
                      icon={<SpaIcon />}
                      label="Tea Tree Oil"
                      color="success"
                      variant="outlined"
                      size="medium"
                      sx={{ mb: 1 }}
                    />
                    <Chip
                      icon={<AccessTimeIcon />}
                      label="60 Day Protocol"
                      color="secondary"
                      variant="outlined"
                      size="medium"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card
                  elevation={4}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    maxWidth: { xs: "100%", sm: "450px", md: "100%" },
                    mx: { xs: "auto", md: 0 },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                    <Typography
                      variant="h5"
                      component="h2"
                      fontWeight={600}
                      mb={2}
                    >
                      My Frustrating Journey
                    </Typography>

                    <List disablePadding>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                          <CheckCircleOutlineIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Spent over $3,000 on medical treatments"
                          secondary="Multiple visits to podiatrists with no results"
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                          <CheckCircleOutlineIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Consulted 4 different podiatrists"
                          secondary="Each with their own approach, but none worked"
                        />
                      </ListItem>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                          <CheckCircleOutlineIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Created my own treatment protocol"
                          secondary="A 60-day system that finally eliminated my warts"
                          primaryTypographyProps={{ fontWeight: "bold" }}
                        />
                      </ListItem>
                    </List>

                    <Button
                      variant="text"
                      color="primary"
                      component="a"
                      href="https://www.tiktok.com/@thewartguy?is_from_webapp=1&sender_device=pc"
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<VideocamIcon />}
                      onClick={() => {
                        amplitude.logEvent("View TikTok", {
                          location: "homepage_hero_secondary",
                        });
                      }}
                      sx={{ mt: 2 }}
                    >
                      See my progress on TikTok
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Treatment Protocol Section */}
        <Container maxWidth="lg">
          <Box sx={{ py: { xs: 4, sm: 6, md: 10 } }}>
            <Grid
              container
              spacing={{ xs: 3, md: 6 }}
              direction={{ xs: "column-reverse", md: "row" }}
            >
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    position: { xs: "static", md: "sticky" },
                    top: 24,
                    mb: { xs: 4, md: 0 },
                  }}
                >
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    The Solution: My 6-Step Protocol
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    After years of failed treatments, I discovered this
                    effective protocol that eliminated my plantar warts in just
                    60 days.
                  </Typography>

                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth={{ xs: true, sm: false }}
                    onClick={() => {
                      amplitude.logEvent("View Treatment", {
                        treatment_name: "Salicylic Acid and Tea Tree Oil",
                        location: "homepage_summary",
                      });
                      navigate("/salicylic-acid-treatment");
                    }}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ mb: 2 }}
                  >
                    See the Complete Protocol
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    fullWidth={{ xs: true, sm: false }}
                    component="a"
                    href="https://linktr.ee/thewartguy"
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LinkIcon />}
                    onClick={() => {
                      amplitude.logEvent("View Linktree", {
                        location: "homepage_summary",
                      });
                    }}
                    sx={{ mb: 4 }}
                  >
                    View All Products Used
                  </Button>

                  <Divider sx={{ mb: 4 }} />

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Follow My Journey
                  </Typography>

                  <Stack
                    spacing={{ xs: 1.5, md: 2 }}
                    direction={{ xs: "column", sm: "row", md: "column" }}
                    sx={{
                      "& > .MuiButton-root": {
                        justifyContent: {
                          xs: "center",
                          sm: "flex-start",
                        },
                        flex: { xs: "1 1 auto", sm: "1", md: "auto" },
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      component="a"
                      href="https://www.tiktok.com/@thewartguy?is_from_webapp=1&sender_device=pc"
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<VideocamIcon />}
                      aria-label="Daily Progress on TikTok"
                      onClick={() => {
                        amplitude.logEvent("View TikTok", {
                          location: "homepage_content",
                        });
                      }}
                    >
                      Daily Progress on TikTok
                    </Button>

                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<DiscordIcon />}
                      component="a"
                      href="https://discord.gg/Fq69xjmDVx"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Get personalized help on Discord"
                      onClick={() => {
                        amplitude.logEvent("Join Discord", {
                          location: "homepage_content",
                        });
                      }}
                    >
                      Personalized Help on Discord
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<LinkIcon />}
                      component="a"
                      href="https://linktr.ee/thewartguy"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="All Products on Linktree"
                      onClick={() => {
                        amplitude.logEvent("View Linktree", {
                          location: "homepage_content",
                        });
                      }}
                    >
                      All Products on Linktree
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  {[
                    {
                      step: 1,
                      title: "Debride the Wart",
                      description:
                        "Soak your foot in hot water for 10-15 minutes to soften the skin, then carefully remove the thick callous or skin on top.",
                      color: "#2B8A91",
                    },
                    {
                      step: 2,
                      title: "Apply Salicylic Acid",
                      description:
                        "Apply a 40% Salicylic Acid solution directly on the wart. Cover with Tegaderm to seal in the treatment.",
                      color: "#FF6B35",
                    },
                    {
                      step: 3,
                      title: "Wait and Debride Again",
                      description:
                        "Wait 12 hours for the acid to work. Remove dead skin and all black spots (blood vessels that feed the wart).",
                      color: "#4CAF50",
                    },
                    {
                      step: 4,
                      title: "Apply Tea Tree Oil",
                      description:
                        "Apply Tea Tree Oil to a cotton pad and place it on the wart overnight. Cover with Tegaderm to keep secure.",
                      color: "#2B8A91",
                    },
                    {
                      step: 5,
                      title: "Debride and Repeat",
                      description:
                        "Remove the Tea Tree Oil, debride using an electric nail file, then apply Salicylic Acid again and repeat.",
                      color: "#FF6B35",
                    },
                    {
                      step: 6,
                      title: "Adjust as Needed",
                      description:
                        "Some days you might apply Salicylic Acid for 24 hours, followed by Tea Tree Oil for another 24 hours.",
                      color: "#2B8A91",
                    },
                  ].map((step, index) => (
                    <Grid item xs={12} key={index}>
                      <Card
                        elevation={2}
                        sx={{
                          p: 0,
                          overflow: "hidden",
                          borderLeft: `4px solid ${step.color}`,
                          transition:
                            "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 12px 20px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                backgroundColor: step.color,
                                color: "white",
                                width: { xs: "28px", sm: "36px" },
                                height: { xs: "28px", sm: "36px" },
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mr: 1.5,
                                fontWeight: "bold",
                                fontSize: { xs: "0.9rem", sm: "1rem" },
                              }}
                            >
                              {step.step}
                            </Box>
                            <Typography
                              variant="h5"
                              component="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: {
                                  xs: "1.1rem",
                                  sm: "1.3rem",
                                  md: "1.5rem",
                                },
                              }}
                            >
                              {step.title}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              ml: { xs: 5, sm: 7 },
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            {step.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </main>
    </>
  );
}

export default Home;
